import { showNotification } from "@mantine/notifications"
import { IUser } from "@features/auth/domain/user.interface.ts"
import { ILoginResponse } from "@features/auth/domain/auth.interfaces.ts"
import { LOCAL_PREFERENCES_PREFIX } from "@shared/constants"

const STORAGE_OF_AUTHORIZATION = "STORAGE_OF_AUTHORIZATION_APP"
const STORAGE_ACCESS_TOKEN = "access_token"

/**
 * Decode a JWT token to get its payload
 * @param token The JWT token to decode
 * @returns The decoded payload or null if the token is invalid
 */
function decodeJwt(token: string): { exp?: number } | null {
  try {
    const parts = token.split(".")
    if (parts.length !== 3) return null

    const base64Url = parts[1]
    if (!base64Url) return null

    // Convert base64url to base64 and add padding if necessary
    let base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/")
    while (base64.length % 4) {
      base64 += "="
    }

    // Decode the base64 string
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    )

    // Parse the JSON payload
    return JSON.parse(jsonPayload)
  } catch (error) {
    console.error("Error decoding JWT token:", error)
    return null
  }
}

export class LocalStorageSession {
  /**
   * Check if the current access token has expired
   * @returns True if the token has expired or is invalid, false otherwise
   */
  static isTokenExpired(): boolean {
    try {
      const token = this.getAccessToken()
      if (!token) return true

      const decoded = decodeJwt(token)
      if (!decoded) return false // Si no podemos decodificarlo, asumimos que no ha expirado o no es un JWT estándar

      const expirationToken = decoded?.exp || 0
      if (!expirationToken) return false // Si no tiene fecha de expiración, no ha expirado

      // exp is in seconds, Date.now() is in milliseconds
      const currentTime = Math.floor(Date.now() / 1000)
      return expirationToken < currentTime
    } catch (error) {
      console.error("Error checking token expiration:", error)
      return false // En caso de error, preferimos intentar la petición que bloquear al usuario
    }
  }

  static saveAuthorization(userInstance: IUser, accessToken?: string): void {
    // Save user to localStorage
    localStorage.setItem("current_user", JSON.stringify(userInstance))

    // Save the access token if provided
    if (accessToken) {
      localStorage.setItem(STORAGE_ACCESS_TOKEN, accessToken)
    }

    // Also save the full authorization data for backward compatibility
    const authData: ILoginResponse = {
      user: userInstance,
      accessToken: accessToken || localStorage.getItem(STORAGE_ACCESS_TOKEN) || ""
    }
    localStorage.setItem(STORAGE_OF_AUTHORIZATION, JSON.stringify(authData))
  }

  static async clearSession(): Promise<void> {
    try {
      const preferences = Object.keys(localStorage)
        .filter((key) => key.startsWith(LOCAL_PREFERENCES_PREFIX))
        .map((key) => [key, localStorage.getItem(key)] as const)
      localStorage.clear()
      preferences.forEach(([key, value]) => value !== null && localStorage.setItem(key, value))
    } catch (error) {
      console.error("Error during logout:", error)
      // Fallback to basic cleanup if the logout process fails
      localStorage.removeItem("current_user")
      localStorage.removeItem(STORAGE_ACCESS_TOKEN)
      localStorage.removeItem(STORAGE_OF_AUTHORIZATION)
      localStorage.removeItem("microsoft_user")
    }
  }

  static getAuthorization(): ILoginResponse {
    const data = localStorage.getItem(STORAGE_OF_AUTHORIZATION)
    if (data == null) {
      // Try to reconstruct from separate storage items
      const userJson = localStorage.getItem("current_user")
      const token = localStorage.getItem(STORAGE_ACCESS_TOKEN)

      if (userJson && token) {
        return {
          user: JSON.parse(userJson),
          accessToken: token
        }
      }

      throw new Error("Required Login")
    }
    return JSON.parse(data)
  }

  static getAccessToken(): string | void {
    // First try to get from dedicated token storage
    const token = localStorage.getItem(STORAGE_ACCESS_TOKEN)
    if (token) return token

    try {
      const authData = this.getAuthorization()
      return authData.accessToken
    } catch {
      showNotification({ color: "red", message: "Error al obtener el token de acceso", title: "Error" })
    }
  }
}
